import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sun, Moon, Bell, User, LogOut, Settings, UserCircle, CreditCard } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { apiFetch } from '../lib/api';

const TopBar = ({ theme, onThemeToggle, searchQuery, setSearchQuery, currentUser, onLogout }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const isFree = currentUser?.account_type === 'free' && !currentUser?.subscription_bypass;

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      // Fetch recent actions as notifications
      const response = await apiFetch('/api/actions');
      const actions = await response.json();
      
      // Filter actions that are due soon or overdue
      const today = new Date();
      const relevantActions = actions
        .filter(a => {
          if (a.status === 'Terminé' || a.status === 'Annulé') return false;
          const dueDate = new Date(a.due_date);
          const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
          return diffDays <= 3; // Due in 3 days or less
        })
        .slice(0, 5)
        .map(a => ({
          id: a.id,
          title: a.title,
          project: a.project_name,
          dueDate: a.due_date,
          type: new Date(a.due_date) < today ? 'overdue' : 'upcoming',
          read: false
        }));
      
      setNotifications(relevantActions);
      setUnreadCount(relevantActions.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const getNotificationStyle = (type) => {
    if (type === 'overdue') return 'border-l-4 border-red-500 bg-red-50';
    return 'border-l-4 border-yellow-500 bg-yellow-50';
  };

  return (
    <div className="sticky top-0 z-40 backdrop-blur-sm bg-background/85 border-b border-border" data-testid="topbar">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        <div className="flex items-center gap-2 sm:gap-4 flex-1">
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher projets, actions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full text-sm"
              data-testid="search-input"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isFree && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/pricing')}
              className="hidden sm:flex items-center gap-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="topbar-upgrade-btn"
            >
              <CreditCard className="w-3.5 h-3.5" />
              Passer au Pro
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onThemeToggle}
            data-testid="theme-toggle"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>

          {/* Notifications Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0 bg-background border shadow-lg" align="end" sideOffset={8}>
              <div className="p-3 border-b border-border flex items-center justify-between bg-background">
                <h3 className="font-semibold text-sm">Notifications</h3>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllAsRead}>
                    Tout marquer comme lu
                  </Button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto bg-background">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground bg-background">
                    Aucune notification
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-3 border-b border-border last:border-0 cursor-pointer transition-colors ${
                        notif.type === 'overdue' 
                          ? 'bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500' 
                          : 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-l-yellow-500'
                      }`}
                      onClick={() => {
                        navigate(`/tasks?action=${notif.id}`);
                      }}
                    >
                      <p className="text-sm font-medium text-foreground">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {notif.project}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Échéance: {notif.dueDate}
                      </p>
                      {notif.type === 'overdue' && (
                        <Badge variant="destructive" className="mt-2 text-xs">En retard</Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="p-2 border-t border-border bg-background">
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm" 
                    onClick={() => navigate('/tasks')}
                  >
                    Voir toutes les actions
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                <User className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-background">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{currentUser?.name || 'Utilisateur'}</p>
                <p className="text-xs text-muted-foreground">{currentUser?.email || ''}</p>
                {isFree ? (
                  <Badge variant="outline" className="mt-1 text-xs border-amber-300 text-amber-700" data-testid="account-badge-free">Gratuit</Badge>
                ) : (
                  <Badge variant="outline" className="mt-1 text-xs border-blue-300 text-blue-700" data-testid="account-badge-pro">Pro</Badge>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => navigate('/profile')}
                data-testid="profile-menu-item"
              >
                <UserCircle className="w-4 h-4 mr-2" />
                Profil
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/settings')}
                data-testid="settings-menu-item"
              >
                <Settings className="w-4 h-4 mr-2" />
                Paramètres
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} data-testid="logout-menu-item">
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
